import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import * as Sentry from "@sentry/react";
import { stores } from "src/App";

// Extend the InternalAxiosRequestConfig & AxiosRequestConfig interface to include additional properties
declare module "axios" {
    export interface InternalAxiosRequestConfig {
        /** This is the sentryTranscation that gets started by `getRequestInterceptorSentryTransactionStart` and finished by `getResponseInterceptorSuccessSentryTranscationFinish`, `getResponseInterceptorErrorSentryTranscationFinish` or `getRequestInterceptorErrorSentryTransactionFinish`   */
        sentryTransaction?: ReturnType<Sentry.Hub["startTransaction"]>;

        /** Set this in order to group sentry transactions without the params */
        urlWithoutParams?: string;
    }

    export interface AxiosRequestConfig {
        /** Set this in order to group sentry transactions without the params */
        urlWithoutParams?: string;
    }
}

const HEADER_AUTH = "Authorization";
const DEFAULT_TIMEOUT = 6000 * 10; // 60 seconds

class Axios {
    private static instance?: AxiosInstance = undefined;
    private static authInstance?: AxiosInstance = undefined;

    public static getInstance() {
        if (!Axios.instance) {
            Axios.instance = axios.create();
            Axios.instance.defaults.timeout = DEFAULT_TIMEOUT;
            Axios.instance.interceptors.request.use(
                Axios.getRequestInterceptorSentryTransactionStart(),
                Axios.getRequestInterceptorErrorSentryTransactionFinish(),
                {
                    synchronous: true
                }
            );

            Axios.instance.interceptors.response.use(
                Axios.getResponseInterceptorSuccessSentryTranscationFinish(),
                Axios.getResponseInterceptorErrorSentryTranscationFinish()
            );
        }

        return Axios.instance;
    }

    public static getAuthInstance() {
        if (!Axios.authInstance) {
            Axios.authInstance = axios.create();
            Axios.authInstance.defaults.timeout = DEFAULT_TIMEOUT;
            Axios.authInstance.interceptors.request.use(
                Axios.getRequestInterceptorSentryTransactionStart(),
                Axios.getRequestInterceptorErrorSentryTransactionFinish(),
                {
                    synchronous: true
                }
            );

            Axios.authInstance.interceptors.request.use(Axios.getRequestInterceptorToken(), null, {
                synchronous: true
            });

            Axios.authInstance.interceptors.response.use(
                Axios.getResponseInterceptorSuccessSentryTranscationFinish(),
                Axios.getResponseInterceptorErrorSentryTranscationFinish()
            );

            Axios.authInstance.interceptors.response.use(
                Axios.getResponseInterceptor401Success(),
                Axios.getResponseInterceptor401Error()
            );
        }

        return Axios.authInstance;
    }

    private static getRequestInterceptorToken() {
        return (config: InternalAxiosRequestConfig) => {
            if (stores.authStore.token) {
                config.headers[HEADER_AUTH] = `${stores.authStore.tokenType} ${stores.authStore.token}`;
            }
            return config;
        };
    }

    private static getRequestInterceptorSentryTransactionStart() {
        return (config: InternalAxiosRequestConfig) => {
            config.sentryTransaction = Sentry.startTransaction({
                name: `${config.urlWithoutParams ? config.urlWithoutParams : config.url}`,
                op: "Axios Request"
            });
            return config;
        };
    }

    private static getRequestInterceptorErrorSentryTransactionFinish() {
        return (error: any) => {
            error?.config?.sentryTransaction?.finish();
            return error;
        };
    }

    private static getResponseInterceptorSuccessSentryTranscationFinish() {
        return (response: AxiosResponse<any, any>) => {
            response.config?.sentryTransaction?.finish();
            return response;
        };
    }

    private static getResponseInterceptorErrorSentryTranscationFinish() {
        return (error: any) => {
            error.config?.sentryTransaction?.finish();
            return Promise.reject(error);
        };
    }

    private static getResponseInterceptor401Success() {
        return (response: AxiosResponse<any, any>) => {
            return response;
        };
    }

    private static getResponseInterceptor401Error() {
        return async (error: any) => {
            const errorResponse = error.response;
            const originalRequest = error.config;
            if (errorResponse?.status === 401) {
                originalRequest._retry = true;
                return stores.authStore.authClient
                    .refresh(stores.authStore.refreshToken)
                    .then(async (reloginSuccess) => {
                        if (reloginSuccess) {
                            console.log("Relogin durch AxiosWrapper mit refreshToken erfolgreich");
                            return Axios.authInstance?.request(originalRequest);
                        } else {
                            return Promise.reject(error);
                        }
                    })
                    .catch((failure: any) => {
                        return Promise.reject(failure);
                    });
            }
            return Promise.reject(error);
        };
    }
}

export default Axios;
